import { assertNever } from "@redotech/util/type";
import {
  HsaAgeAnswer,
  HsaAnswer,
  HsaAnswerKind,
  HsaMultiSelectAnswer,
  HsaSingleSelectAnswer,
  HsaSingleSelectOtherAnswer,
  HsaTextAnswer,
  hsaAnswerKindForQuestionType,
} from "./hsa-answer";
import { ageInWholeYears } from "./hsa-flow-run";
import {
  HsaAgeInputQuestion,
  HsaDateInputQuestion,
  HsaDynamicMultiSelectQuestion,
  HsaEmailInputQuestion,
  HsaMultiSelectNoneQuestion,
  HsaMultiSelectQuestion,
  HsaPhoneInputQuestion,
  HsaQuestion,
  HsaQuestionType,
  HsaSingleSelectDropdownQuestion,
  HsaSingleSelectOtherQuestion,
  HsaSingleSelectRadioQuestion,
  HsaTextInputQuestion,
  MULTI_SELECT_NONE_OPTION_VALUE,
  SINGLE_SELECT_OTHER_OPTION_VALUE,
} from "./hsa-question-type";

export enum HsaAnswerValidationErrorCode {
  KIND_MISMATCH = "kind_mismatch",
  // Age
  AGE_NOT_INTEGER = "age_not_integer",
  AGE_OUT_OF_RANGE = "age_out_of_range",
  // Email
  EMAIL_INVALID = "email_invalid",
  // Text
  TEXT_REGEX_MISMATCH = "text_regex_mismatch",
  TEXT_REQUIRED = "text_required",
  // Date
  DATE_INVALID = "date_invalid",
  // Phone
  PHONE_INVALID = "phone_invalid",
  // Single select
  OPTION_VALUE_NOT_FOUND = "option_value_not_found",
  // Single-select-other
  OTHER_TEXT_REQUIRED = "other_text_required",
  OTHER_TEXT_UNEXPECTED = "other_text_unexpected",
  // Multi-select
  DUPLICATE_SELECTION = "duplicate_selection",
  SELECTION_COUNT_TOO_FEW = "selection_count_too_few",
  SELECTION_COUNT_TOO_MANY = "selection_count_too_many",
  // Multi-select-none specific
  NONE_NOT_EXCLUSIVE = "none_not_exclusive",
}

export interface HsaAnswerValidationError {
  code: HsaAnswerValidationErrorCode;
  questionHandle: string;
  message: string;
}

// Age range is baked into AGE_INPUT_QUESTION semantics; same for everyone.
const AGE_MIN = 0;
const AGE_MAX = 200;

// Lifted from RFC-5322-ish patterns matching TrueMed's. Validates length
// constraints (1–254 total, 1–64 local-part) plus standard local + domain
// shape. Baked into EMAIL_INPUT_QUESTION semantics.
const EMAIL_REGEX =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]$/;

// ISO calendar date, YYYY-MM-DD.
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// Oldest plausible date-of-birth age; anything older is a typo in the year.
const DATE_OF_BIRTH_MAX_AGE = 110;

// E.164: leading +, first digit 1–9, up to 15 digits total.
const E164_PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

// Validates a submitted answer against its pool question. Does NOT handle
// "required question was never answered" — that's a flow-level check
// (caller looks at HsaAnswerMap and the current group's questionHandles).
// This function assumes an answer exists and is being checked.
export function validateHsaAnswer(
  question: HsaQuestion,
  questionHandle: string,
  answer: HsaAnswer,
): HsaAnswerValidationError[] {
  const errors: HsaAnswerValidationError[] = [];
  const expectedKind = hsaAnswerKindForQuestionType(question.type);
  if (expectedKind !== answer.kind) {
    errors.push({
      code: HsaAnswerValidationErrorCode.KIND_MISMATCH,
      questionHandle,
      message: `Answer kind "${answer.kind}" does not match the expected kind "${expectedKind}" for question type "${question.type}".`,
    });
    return errors;
  }
  switch (question.type) {
    case HsaQuestionType.AGE_INPUT_QUESTION: {
      if (answer.kind !== HsaAnswerKind.AGE) {
        return errors;
      }
      validateAgeAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.EMAIL_INPUT_QUESTION: {
      if (answer.kind !== HsaAnswerKind.TEXT) {
        return errors;
      }
      validateEmailAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.TEXT_INPUT_QUESTION: {
      if (answer.kind !== HsaAnswerKind.TEXT) {
        return errors;
      }
      validateTextAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.DATE_INPUT_QUESTION: {
      if (answer.kind !== HsaAnswerKind.TEXT) {
        return errors;
      }
      validateDateAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.PHONE_INPUT_QUESTION: {
      if (answer.kind !== HsaAnswerKind.TEXT) {
        return errors;
      }
      validatePhoneAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.SINGLE_SELECT_RADIO_QUESTION:
    case HsaQuestionType.SINGLE_SELECT_DROPDOWN_QUESTION: {
      if (answer.kind !== HsaAnswerKind.SINGLE_SELECT) {
        return errors;
      }
      validateSingleSelectAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.SINGLE_SELECT_OTHER_QUESTION: {
      if (answer.kind !== HsaAnswerKind.SINGLE_SELECT_OTHER) {
        return errors;
      }
      validateSingleSelectOtherAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.MULTI_SELECT_QUESTION: {
      if (answer.kind !== HsaAnswerKind.MULTI_SELECT) {
        return errors;
      }
      validateMultiSelectAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.MULTI_SELECT_NONE_QUESTION: {
      if (answer.kind !== HsaAnswerKind.MULTI_SELECT) {
        return errors;
      }
      validateMultiSelectNoneAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    case HsaQuestionType.DYNAMIC_MULTI_SELECT_QUESTION: {
      if (answer.kind !== HsaAnswerKind.MULTI_SELECT) {
        return errors;
      }
      validateMultiSelectAnswer(question, questionHandle, answer, errors);
      return errors;
    }
    default: {
      assertNever(question);
    }
  }
}

function validateAgeAnswer(
  _question: HsaAgeInputQuestion,
  questionHandle: string,
  answer: HsaAgeAnswer,
  errors: HsaAnswerValidationError[],
): void {
  if (!Number.isInteger(answer.value)) {
    errors.push({
      code: HsaAnswerValidationErrorCode.AGE_NOT_INTEGER,
      questionHandle,
      message: `Age answer must be an integer; got ${answer.value}.`,
    });
    return;
  }
  if (answer.value < AGE_MIN || answer.value > AGE_MAX) {
    errors.push({
      code: HsaAnswerValidationErrorCode.AGE_OUT_OF_RANGE,
      questionHandle,
      message: `Age answer ${answer.value} is out of range [${AGE_MIN}, ${AGE_MAX}].`,
    });
  }
}

function validateEmailAnswer(
  question: HsaEmailInputQuestion,
  questionHandle: string,
  answer: HsaTextAnswer,
  errors: HsaAnswerValidationError[],
): void {
  if (answer.text.length === 0) {
    if (question.required) {
      errors.push({
        code: HsaAnswerValidationErrorCode.TEXT_REQUIRED,
        questionHandle,
        message: "Email answer is empty but the question is required.",
      });
    }
    return;
  }
  if (!EMAIL_REGEX.test(answer.text)) {
    errors.push({
      code: HsaAnswerValidationErrorCode.EMAIL_INVALID,
      questionHandle,
      message: `Email answer "${answer.text}" is not a valid email address.`,
    });
  }
}

function validateTextAnswer(
  question: HsaTextInputQuestion,
  questionHandle: string,
  answer: HsaTextAnswer,
  errors: HsaAnswerValidationError[],
): void {
  if (answer.text.length === 0) {
    if (question.required) {
      errors.push({
        code: HsaAnswerValidationErrorCode.TEXT_REQUIRED,
        questionHandle,
        message: "Text answer is empty but the question is required.",
      });
    }
    return;
  }
  if (question.validationRegex !== undefined) {
    let regex: RegExp;
    try {
      regex = new RegExp(question.validationRegex);
    } catch {
      // Invalid regex on the pool entry. Treat as no validation rather
      // than rejecting the answer; the pool integrity check will surface
      // the bad regex separately.
      return;
    }
    if (!regex.test(answer.text)) {
      errors.push({
        code: HsaAnswerValidationErrorCode.TEXT_REGEX_MISMATCH,
        questionHandle,
        message:
          question.validationMessage ??
          `Text answer "${answer.text}" does not match the question's validation pattern.`,
      });
    }
  }
}

function validateDateAnswer(
  question: HsaDateInputQuestion,
  questionHandle: string,
  answer: HsaTextAnswer,
  errors: HsaAnswerValidationError[],
): void {
  if (answer.text.length === 0) {
    if (question.required) {
      errors.push({
        code: HsaAnswerValidationErrorCode.TEXT_REQUIRED,
        questionHandle,
        message: "Date answer is empty but the question is required.",
      });
    }
    return;
  }
  if (!isValidIsoDate(answer.text)) {
    errors.push({
      code: HsaAnswerValidationErrorCode.DATE_INVALID,
      questionHandle,
      message: `Date answer "${answer.text}" is not a valid YYYY-MM-DD date.`,
    });
    return;
  }
  // A DATE answer is a date of birth. Reject impossible ones inline so the
  // shopper can fix the typo; the 18+ rule stays a flow knockout. These reuse
  // DATE_INVALID because it is a wire enum that cached clients parse strictly.
  const age = ageInWholeYears(answer.text, new Date());
  if (age !== null && age < 0) {
    errors.push({
      code: HsaAnswerValidationErrorCode.DATE_INVALID,
      questionHandle,
      message: "Please enter a date of birth in the past.",
    });
  } else if (age !== null && age > DATE_OF_BIRTH_MAX_AGE) {
    errors.push({
      code: HsaAnswerValidationErrorCode.DATE_INVALID,
      questionHandle,
      message: "Please check the year of your date of birth.",
    });
  }
}

function validatePhoneAnswer(
  question: HsaPhoneInputQuestion,
  questionHandle: string,
  answer: HsaTextAnswer,
  errors: HsaAnswerValidationError[],
): void {
  if (answer.text.length === 0) {
    if (question.required) {
      errors.push({
        code: HsaAnswerValidationErrorCode.TEXT_REQUIRED,
        questionHandle,
        message: "Phone answer is empty but the question is required.",
      });
    }
    return;
  }
  // The phone field stores the human-formatted value (e.g. "+1 (555) 123-4567");
  // strip formatting before the E.164 check. The Gale mapper normalizes too.
  const normalized = answer.text.replace(/[^\d+]/g, "");
  if (!E164_PHONE_REGEX.test(normalized)) {
    errors.push({
      code: HsaAnswerValidationErrorCode.PHONE_INVALID,
      questionHandle,
      message: `Phone answer "${answer.text}" is not a valid E.164 phone number.`,
    });
  }
}

// Rejects malformed strings and impossible calendar dates (e.g. 2021-02-30)
// by round-tripping through Date and checking the components survive.
function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_REGEX.test(value)) {
    return false;
  }
  const parts = value.split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function validateSingleSelectAnswer(
  question: HsaSingleSelectRadioQuestion | HsaSingleSelectDropdownQuestion,
  questionHandle: string,
  answer: HsaSingleSelectAnswer,
  errors: HsaAnswerValidationError[],
): void {
  const valid = question.options.some((o) => o.value === answer.optionValue);
  if (!valid) {
    errors.push({
      code: HsaAnswerValidationErrorCode.OPTION_VALUE_NOT_FOUND,
      questionHandle,
      message: `Selected optionValue "${answer.optionValue}" is not one of the question's options.`,
    });
  }
}

function validateSingleSelectOtherAnswer(
  question: HsaSingleSelectOtherQuestion,
  questionHandle: string,
  answer: HsaSingleSelectOtherAnswer,
  errors: HsaAnswerValidationError[],
): void {
  const valid = question.options.some((o) => o.value === answer.optionValue);
  if (!valid) {
    errors.push({
      code: HsaAnswerValidationErrorCode.OPTION_VALUE_NOT_FOUND,
      questionHandle,
      message: `Selected optionValue "${answer.optionValue}" is not one of the question's options.`,
    });
    return;
  }
  const isOther = answer.optionValue === SINGLE_SELECT_OTHER_OPTION_VALUE;
  if (
    isOther &&
    (answer.otherText === undefined || answer.otherText.trim().length === 0)
  ) {
    errors.push({
      code: HsaAnswerValidationErrorCode.OTHER_TEXT_REQUIRED,
      questionHandle,
      message: `Selected option is "${SINGLE_SELECT_OTHER_OPTION_VALUE}" but otherText is missing or empty.`,
    });
  }
  if (!isOther && answer.otherText !== undefined) {
    errors.push({
      code: HsaAnswerValidationErrorCode.OTHER_TEXT_UNEXPECTED,
      questionHandle,
      message: `otherText is set but the selected option is "${answer.optionValue}", not "${SINGLE_SELECT_OTHER_OPTION_VALUE}". Drop otherText.`,
    });
  }
}

function validateMultiSelectAnswer(
  question: HsaMultiSelectQuestion | HsaDynamicMultiSelectQuestion,
  questionHandle: string,
  answer: HsaMultiSelectAnswer,
  errors: HsaAnswerValidationError[],
): void {
  validateMultiSelectShared(question.options, questionHandle, answer, errors);
  validateSelectionCount(question, questionHandle, answer, errors);
}

function validateMultiSelectNoneAnswer(
  question: HsaMultiSelectNoneQuestion,
  questionHandle: string,
  answer: HsaMultiSelectAnswer,
  errors: HsaAnswerValidationError[],
): void {
  validateMultiSelectShared(question.options, questionHandle, answer, errors);
  // "none" is exclusive — if it appears, nothing else may.
  if (
    answer.optionValues.includes(MULTI_SELECT_NONE_OPTION_VALUE) &&
    answer.optionValues.length > 1
  ) {
    errors.push({
      code: HsaAnswerValidationErrorCode.NONE_NOT_EXCLUSIVE,
      questionHandle,
      message: `Answer includes "${MULTI_SELECT_NONE_OPTION_VALUE}" alongside other selections. "${MULTI_SELECT_NONE_OPTION_VALUE}" is exclusive — submit it alone or omit it.`,
    });
  }
}

function validateMultiSelectShared(
  options: { value: string; label: string }[],
  questionHandle: string,
  answer: HsaMultiSelectAnswer,
  errors: HsaAnswerValidationError[],
): void {
  const validValues = new Set(options.map((o) => o.value));
  const seen = new Set<string>();
  for (const value of answer.optionValues) {
    if (!validValues.has(value)) {
      errors.push({
        code: HsaAnswerValidationErrorCode.OPTION_VALUE_NOT_FOUND,
        questionHandle,
        message: `Selected optionValue "${value}" is not one of the question's options.`,
      });
    }
    if (seen.has(value)) {
      errors.push({
        code: HsaAnswerValidationErrorCode.DUPLICATE_SELECTION,
        questionHandle,
        message: `Selected optionValue "${value}" appears more than once.`,
      });
    }
    seen.add(value);
  }
}

function validateSelectionCount(
  question: HsaMultiSelectQuestion | HsaDynamicMultiSelectQuestion,
  questionHandle: string,
  answer: HsaMultiSelectAnswer,
  errors: HsaAnswerValidationError[],
): void {
  const count = answer.optionValues.length;
  if (question.minSelections !== undefined && count < question.minSelections) {
    errors.push({
      code: HsaAnswerValidationErrorCode.SELECTION_COUNT_TOO_FEW,
      questionHandle,
      message: `Answer has ${count} selection(s); question requires at least ${question.minSelections}.`,
    });
  }
  if (question.maxSelections !== undefined && count > question.maxSelections) {
    errors.push({
      code: HsaAnswerValidationErrorCode.SELECTION_COUNT_TOO_MANY,
      questionHandle,
      message: `Answer has ${count} selection(s); question allows at most ${question.maxSelections}.`,
    });
  }
}
